import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { Popover } from 'antd';
import { observer } from 'mobx-react';
import { QuestionAnalysis } from '@/components/question';
import { IQuestionListItem, IUserAnswerMap, QuestionType } from '@/types';
import { AnswerCardIcon, QuestionRemoveBtn } from '@/svgs';
import './index.less';
import AnswerSheet from '../paper/answer-sheet';
import { useCreation, useMount } from 'ahooks';
import { initialQuestionList } from '../paper/utils';
import { subQuestionSortNumProjectIds } from '@/utils/config';

interface IProps {
    questionIds: number[];
    questionList: IQuestionListItem[];
    projectId: number;
    title?: string;
    deleteQuestionRecord?: () => void;
    questionOptions?: any;
}

interface IAnalysisMap {
    [questionId: number]: IQuestionListItem;
}

const SeeAnalysis: React.FC<IProps> = props => {
    const { questionIds, questionList, title, questionOptions } = props;

    const [activeIndex, setActiveIndex] = useState<number[]>([0, 0]);

    const [userAnswerMap, setUserAnswerMap] = useState<IUserAnswerMap>({});

    const [answerSheetOpen, setAnswerSheetOpen] = useState(false);

    // 计算题目总数量 目前公务员根据小题计算， 其余大题
    const allQuestionNum = useCreation(() => {
        if (subQuestionSortNumProjectIds.includes(props.projectId)) {
            return questionList.reduce((prev, current) => {
                if (current.questionType === QuestionType.comprehensive) {
                    return prev + (current.subQuestionList?.length || 0);
                }
                return prev + 1;
            }, 0);
        }
        return questionList.length;
    }, [questionList]);

    // 当前题目
    const currentQuestion = useMemo(() => {
        const question = questionList[activeIndex[0]] || questionList[0];
        return {
            ...question,
        };
    }, [questionList, activeIndex]);

    // 当前小题题目
    const currentSubQuestion = useMemo(() => {
        const question = currentQuestion.subQuestionList?.[activeIndex[1]];
        return question
            ? {
                  ...question,
              }
            : null;
    }, [currentQuestion, activeIndex]);

    /* 是否最后一题 */
    const isLastQuestion = useMemo(() => {
        return Boolean(
            activeIndex[0] === questionIds.length - 1 &&
                (!currentSubQuestion || (currentSubQuestion && activeIndex[1] + 1 === currentQuestion.subQuestionList.length))
        );
    }, [currentQuestion, questionIds.length, currentSubQuestion]);

    /* 是否第一题 */
    const isFirstQuestion = useMemo(() => {
        return Boolean(activeIndex[0] === 0 && (!currentSubQuestion || (currentSubQuestion && activeIndex[1] === 0)));
    }, [activeIndex, currentQuestion, currentSubQuestion]);

    const moveToNextQuestion = () => {
        // 当前题是综合题

        const [questionIndex, subQuestionIndex] = activeIndex;

        // 处理综合题小题
        if (currentSubQuestion && subQuestionIndex < currentQuestion.subQuestionList.length - 1) {
            setActiveIndex([activeIndex[0], subQuestionIndex + 1]);
            return;
        }
        const newQuestion = questionList[questionIndex + 1];
        if (newQuestion) {
            setActiveIndex(() => {
                if (newQuestion.questionType === QuestionType.comprehensive && newQuestion.subQuestionList?.length) {
                    return [questionIndex + 1, 0];
                }
                return [questionIndex + 1];
            });
            return;
        }
    };

    const moveToPrevQuestion = () => {
        // 当前题是综合题
        const [questionIndex, subQuestionIndex] = activeIndex;

        // 处理综合题小题
        if (currentSubQuestion && subQuestionIndex > 0) {
            setActiveIndex([activeIndex[0], subQuestionIndex - 1]);
            return;
        }

        const newQuestion = questionList[questionIndex - 1];
        if (newQuestion) {
            setActiveIndex(() => {
                if (newQuestion.questionType === QuestionType.comprehensive && newQuestion.subQuestionList?.length) {
                    return [questionIndex - 1, newQuestion.subQuestionList.length - 1];
                }
                return [questionIndex - 1];
            });
            return;
        }
    };

    const onChangeActiveQuestion = (questionId: number, subQuestionId: number) => {
        const questionItem = questionList.find(item => item.questionId === questionId);
        const questionIndex = questionList.findIndex(item => item.questionId === questionId) || 0;
        const sonQuestionIndex = questionItem?.subQuestionList?.findIndex(item => item.questionId === subQuestionId) || 0;
        setActiveIndex([questionIndex, sonQuestionIndex]);
    };

    useMount(() => {
        const { initializeUserAnswerMap } = initialQuestionList(questionList);
        setUserAnswerMap(initializeUserAnswerMap);
        console.log('initializeUserAnswerMap', initializeUserAnswerMap);
    });

    return (
        <div className="sub-tiku__see-analysis-layout">
            <header className="sub-tiku__do-question-layout-header">
                <div className="sub-tiku__do-question-layout-header-title">
                    <div className="sub-tiku__do-question-layout-header-name">{title || '知识点'}</div>
                    <div className="sub-tiku__do-question-layout-header-count">共{allQuestionNum}道</div>
                </div>
                <div className="sub-tiku__do-question-layout-header-actions">
                    {props.deleteQuestionRecord && (
                        <div className="sub-tiku__answer-card-btn" onClick={props.deleteQuestionRecord}>
                            <QuestionRemoveBtn />
                            <div>清除记录</div>
                        </div>
                    )}

                    <Popover
                        content={
                            <AnswerSheet
                                isAnalysis
                                moduleList={[]}
                                onChangeActiveQuestion={onChangeActiveQuestion}
                                userAnswerMap={userAnswerMap}
                                questionList={questionList}
                                showModule={false}
                            />
                        }
                        mouseLeaveDelay={1}
                        onOpenChange={newValue => setAnswerSheetOpen(newValue)}
                        overlayClassName="sub-tiku__answer-sheet-popover"
                        placement="bottomRight"
                        trigger="click"
                        open={answerSheetOpen}
                    >
                        <div className="sub-tiku__answer-card-btn">
                            <AnswerCardIcon />
                            <div>答题卡</div>
                        </div>
                    </Popover>
                </div>
            </header>
            <div className="sub-tiku__do-question-layout-content">
                <div id="sub-tiku__question-draft-content">
                    {currentQuestion && <QuestionAnalysis questionItem={currentQuestion} {...questionOptions} />}
                    {currentSubQuestion && <QuestionAnalysis questionItem={currentSubQuestion} {...questionOptions} />}
                </div>
            </div>
            <footer className="sub-tiku__do-question-layout-footer">
                <div className="sub-tiku__fixed-left"></div>
                <div className="sub-tiku__fixed-middle">
                    <div
                        className={classNames('sub-tiku__tiku-default-btn', {
                            disabled: isFirstQuestion,
                        })}
                        onClick={moveToPrevQuestion}
                    >
                        上一题
                    </div>
                    <div
                        className={classNames('sub-tiku__tiku-default-btn sub-tiku__tiku-plain-btn', {
                            disabled: isLastQuestion,
                        })}
                        onClick={moveToNextQuestion}
                    >
                        下一题
                    </div>
                </div>
                <div className="sub-tiku__fixed-right"></div>
            </footer>
        </div>
    );
};

export default observer(SeeAnalysis);
